import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http, { authHttp } from "../api/http";
import User from "../types/user";
import SearchResults from "../types/searchResults";
import SearchParams, { defaultParams } from "../types/searchParams";
import getSearchQueryString from "../helpers/getSearchQueryString";
import { Applications } from "../types/company";
import KeyValue from "../types/keyValue";

interface UserState {
  user: User;
  users: User[];
  userSearchResults?: SearchResults;
  userFilter: SearchParams;
  simpleUserList: KeyValue[];
  status: "idle" | "loading" | "failed";
}

const initialState: UserState = {
  user: {
    id: 0,
    gmdId: 0,
    userId: "",
    firstName: "",
    lastName: "",
    email: "",
    roles: [],
  },
  users: [],
  userFilter: defaultParams,
  userSearchResults: undefined,
  simpleUserList: [],
  status: "idle",
};

export const getUserId = createAsyncThunk<User, number>(
  "user/get/id",
  async (id: number) => {
    const response = await http.get<User>(`/user/${id}`);
    return response.data;
  },
);

export const editUser = createAsyncThunk<User, User>(
  "user/edit",
  async (user: User) => {
    const response = await http.put<User>(`/user/${user.id}`, user, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  },
);

export const deleteUser = createAsyncThunk<User, number>(
  "user/delete",
  async (id: number) => {
    const response = await http.delete<User>(`/user/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  },
);

export const postUser = createAsyncThunk<User, User>(
  "user/post",
  async (user: User) => {
    const response = await http.post<User>(`/user`, user, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  },
);

export const searchUsers = createAsyncThunk<SearchResults, SearchParams>(
  "user/search",
  async (search: SearchParams) => {
    let qs = getSearchQueryString(search);
    const response = await http.get<SearchResults>(`/user/search?${qs}`);
    return response.data;
  },
);

export const getUsers = createAsyncThunk<User[]>("users/get", async () => {
  const [profileResponse, dbResponse] = await Promise.all([
    authHttp.get<Partial<User>[]>(`/user?appId=${Applications.Gmd}`),
    http.get<Partial<User>[]>(`/user/all`),
  ]);

  const profileUsers = profileResponse.data;
  const dbUsers = dbResponse.data;

  const mergedMap: Record<string, User> = {};

  for (const profile of profileUsers) {
    if (!profile.userId) continue;
    mergedMap[profile.userId] = { ...profile } as User;
  }

  for (const db of dbUsers) {
    if (!db.userId) continue;
    if (mergedMap[db.userId]) {
      mergedMap[db.userId] = {
        ...mergedMap[db.userId],
        ...db,
      };
    } else {
      mergedMap[db.userId] = { ...db } as User;
    }
  }

  return Object.values(mergedMap);
});

export const getSimpleUsers = createAsyncThunk<KeyValue[], boolean>(
  "users/get-simple",
  async (deleted: boolean) => {
    const [profileResponse, dbResponse] = await Promise.all([
      authHttp.get<KeyValue[]>(`user/app/3?deleted=${deleted}`),
      http.get<Partial<User>[]>(`/user/all`),
    ]);
    const profileUsers = profileResponse.data;
    const dbUsers = dbResponse.data;

    const mergedMap: Record<string, User> = {};
    const result: KeyValue[] = [];

    for (const profile of profileUsers) {
      if (!profile.stringId) continue;
      mergedMap[profile.stringId] = { ...profile } as User;
    }

    for (const db of dbUsers) {
      if (!db.userId) continue;
      if (mergedMap[db.userId]) {
        mergedMap[db.userId] = {
          ...mergedMap[db.userId],
          ...db,
        };
      } else {
        mergedMap[db.userId] = { ...db } as User;
      }
    }

    for (const prop in mergedMap) {
      if (!!mergedMap[prop].gmdId && !!mergedMap[prop].value) {
        const mappedUser: KeyValue = {
          id: mergedMap[prop].gmdId,
          value: mergedMap[prop].value,
        };
        result.push(mappedUser);
      }
    }
    return result;
  },
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    resetUser(state) {
      state.user = { ...initialState.user };
    },
    setUserFilter(state, action) {
      state.userFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getUserId.fulfilled, (state, action) => {
        state.status = "idle";
        state.user = action.payload;
      })
      .addCase(getUserId.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editUser.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(editUser.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(deleteUser.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postUser.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(postUser.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchUsers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.status = "idle";
        state.userSearchResults = action.payload;
      })
      .addCase(searchUsers.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getUsers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.status = "idle";
        state.users = action.payload.filter(
          (u) => !!u.firstName && !!u.lastName && !!u.email && !!u.gmdId,
        );
      })
      .addCase(getUsers.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getSimpleUsers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSimpleUsers.fulfilled, (state, action) => {
        state.status = "idle";
        state.simpleUserList = action.payload;
      })
      .addCase(getSimpleUsers.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default userSlice.reducer;
export const { resetUser, setUserFilter } = userSlice.actions;
